import { z } from "zod";

import {
  OrganisationIdSchema,
  SessionContextKindSchema,
  UserIdSchema,
} from "@thalamos/haven-common";

const STORAGE_KEYS = {
  userId: "haven-session-user-id",
  sessionContextOrganisationId: "haven-session-context-id",
  kind: "haven-session-context-kind",
} as const;

/**
 * The identity a deep link was issued to.
 *
 * Built from the shared schemas rather than declared as plain strings, so the
 * ids carry their brands and a caller cannot compare a user id against an
 * organisation id by accident. `sessionContextOrganisationId` is nullable
 * because a session may legitimately be acting in no context yet.
 */
export const SessionIdentitySchema = z.object({
  userId: UserIdSchema,
  sessionContextOrganisationId: OrganisationIdSchema.nullable(),
  kind: SessionContextKindSchema,
});

export type SessionIdentity = z.infer<typeof SessionIdentitySchema>;

/**
 * Whether the signed-in identity differs from the one this browser last saw.
 *
 * A deep link is only safe to follow for the identity it was issued to. If a
 * different user — or the same user in a different kind of work — arrives at
 * a stored destination, they may have no business seeing it, so the caller
 * discards the link and starts at the home page instead.
 *
 * Absence is not a change: a first visit, or a clean sign-out that cleared the
 * record, has nothing to contradict and must not throw the destination away.
 */
export const identityHasChanged = (current: SessionIdentity): boolean => {
  const previous = readSessionIdentity();

  if (previous === undefined) {
    return false;
  }

  return (
    previous.userId !== current.userId ||
    previous.sessionContextOrganisationId !==
      current.sessionContextOrganisationId ||
    previous.kind !== current.kind
  );
};

/**
 * Parsed rather than trusted, so downstream code holding a `SessionIdentity`
 * knows the ids are ids. A record written by an older build may not fit this
 * shape; that reads as no record, which is the same risk as a first visit
 * rather than a new one.
 */
export const readSessionIdentity = (): SessionIdentity | undefined => {
  const parsed = SessionIdentitySchema.safeParse({
    userId: safeRead(STORAGE_KEYS.userId),
    sessionContextOrganisationId: safeRead(
      STORAGE_KEYS.sessionContextOrganisationId,
    ),
    kind: safeRead(STORAGE_KEYS.kind),
  });

  return parsed.success ? parsed.data : undefined;
};

export const writeSessionIdentity = (identity: SessionIdentity): void => {
  safeWrite(STORAGE_KEYS.userId, identity.userId);
  safeWrite(STORAGE_KEYS.kind, identity.kind);

  // Absence is stored as absence rather than as an empty string: a sentinel
  // value would read back as a context whose id happens to be blank.
  if (identity.sessionContextOrganisationId === null) {
    safeRemove(STORAGE_KEYS.sessionContextOrganisationId);
  } else {
    safeWrite(
      STORAGE_KEYS.sessionContextOrganisationId,
      identity.sessionContextOrganisationId,
    );
  }
};

export const clearSessionIdentity = (): void => {
  for (const key of Object.values(STORAGE_KEYS)) {
    safeRemove(key);
  }
};

/**
 * Storage throws outright in some browsers rather than returning null — private
 * windows, blocked site data. Losing this record only costs a deep link, so
 * every access swallows the failure: the alternative is that signing out, or
 * following a link, fails for someone whose browser will not store anything.
 */
const safeRead = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** See {@link safeRead} for why this cannot be allowed to throw. */
const safeWrite = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Losing the record costs a deep link and nothing else.
  }
};

/** See {@link safeRead} for why this cannot be allowed to throw. */
const safeRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // A record that cannot be cleared is a record that was never written.
  }
};
